import { useAppStore } from "../stores/store";

function evaluateHasPrivilege(
  requiredPrivileges: string | string[],
  mode: 'all' | 'any',
  userPrivileges: string[],
): boolean {
  const required = Array.isArray(requiredPrivileges) ? requiredPrivileges : [requiredPrivileges];
  if (required.length === 0) return true;

  if (mode === 'any') {
    return required.some((p) => userPrivileges.includes(p));
  }
  return required.every((p) => userPrivileges.includes(p));
}

/**
 * Hook to check if current user has a specific privilege or set of privileges
 * @param requiredPrivileges - Single privilege string or array of privileges
 * @param mode - 'all' (must have all) or 'any' (must have at least one)
 *
 * Note: Does not distinguish "still loading" from "loaded but missing". For
 * UIs that need to show a loading state on cold refresh, prefer
 * {@link useHasPrivilegeStatus}.
 */
export function useHasPrivilege(
  requiredPrivileges: string | string[],
  mode: 'all' | 'any' = 'any'
): boolean {
  const userPrivileges = useAppStore((state) => state.ui?.privileges) || [];
  return evaluateHasPrivilege(requiredPrivileges, mode, userPrivileges);
}

export type HasPrivilegeStatus = {
  /** True when the user actually has the required privilege(s). False both before privileges load and when truly missing. */
  has: boolean;
  /** True once `/authz/privileges` has resolved at least once for the current session. */
  loaded: boolean;
};

/**
 * Same check as {@link useHasPrivilege} but also returns whether privileges
 * have been loaded yet. Use this when the UI should show a loading state
 * on cold page refresh instead of falsely showing a "no permission" state
 * before `/authz/privileges` returns.
 */
export function useHasPrivilegeStatus(
  requiredPrivileges: string | string[],
  mode: 'all' | 'any' = 'any'
): HasPrivilegeStatus {
  const userPrivileges = useAppStore((state) => state.ui?.privileges) || [];
  const loaded = useAppStore((state) => state.ui?.privilegesLoaded) ?? false;
  const has = evaluateHasPrivilege(requiredPrivileges, mode, userPrivileges);
  return { has, loaded };
}
